// Editor for one DMR / analog codeplug (zones, channels, contacts, RX group lists). Used for a
// network's profiles (Admin > General) and for one radio's own copy (Admin > Radios > Edit >
// Radio-Specific Settings > DMR settings). It only edits the value; the caller saves it.
import { useEffect, useState } from 'react';
import type { Codeplug, CodeplugChannel, CodeplugContact, ChannelMode, ToneType } from '../../services/dmrCodeplug';
import {
  CTCSS_TONES,
  DCS_CODES,
  LIMITS,
  codeplugProblems,
  deleteChannel,
  deleteContact,
  deleteRxGroupList,
  deleteZone,
  hzFromMhz,
  mhz,
  moveZone,
  newChannel,
  newContact,
  withMode,
  withTone,
} from './dmrCodeplugModel';

type Tab = 'zones' | 'channels' | 'contacts' | 'lists';

const input = 'px-2 py-1 bg-gray-900 border border-gray-600 rounded text-white text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent disabled:opacity-50';
const smallBtn = 'px-2 py-1 rounded text-xs font-semibold disabled:opacity-40';

/** A frequency field in MHz that keeps the typed text until it is left (Hz in the value). */
function MhzInput({ hz, onChange, label, disabled }: { hz: number; onChange: (hz: number) => void; label: string; disabled?: boolean }) {
  const [text, setText] = useState(mhz(hz));
  useEffect(() => setText(mhz(hz)), [hz]);
  return (
    <input
      aria-label={label}
      className={`${input} w-28`}
      value={text}
      placeholder="MHz"
      disabled={disabled}
      inputMode="decimal"
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onChange(hzFromMhz(text))}
    />
  );
}

function NumberChips({
  values,
  label,
  options,
  max,
  onChange,
  disabled,
}: {
  values: number[];
  label: (n: number) => string;
  options: Array<{ value: number; label: string }>;
  max: number;
  onChange: (values: number[]) => void;
  disabled?: boolean;
}) {
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= values.length) return;
    const next = [...values];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const free = options.filter((o) => !values.includes(o.value));
  return (
    <div className="flex flex-wrap items-center gap-1">
      {values.map((n, i) => (
        <span key={`${n}-${i}`} className="inline-flex items-center gap-1 rounded bg-gray-700 px-2 py-0.5 text-xs text-gray-100">
          {label(n)}
          {!disabled && (
            <>
              <button type="button" className="text-gray-400 hover:text-white" aria-label="Move left" onClick={() => move(i, -1)}>‹</button>
              <button type="button" className="text-gray-400 hover:text-white" aria-label="Move right" onClick={() => move(i, 1)}>›</button>
              <button type="button" className="text-red-300 hover:text-red-100" aria-label="Remove" onClick={() => onChange(values.filter((_, k) => k !== i))}>×</button>
            </>
          )}
        </span>
      ))}
      {!disabled && values.length < max && free.length > 0 && (
        <select className={`${input} text-xs`} value="" onChange={(e) => e.target.value && onChange([...values, Number(e.target.value)])} aria-label="Add">
          <option value="">+ Add…</option>
          {free.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      )}
      <span className="text-xs text-gray-500">{values.length}/{max}</span>
    </div>
  );
}

const TONE_TYPES: Array<{ value: ToneType; label: string }> = [
  { value: 'NONE', label: 'None' },
  { value: 'CTCSS', label: 'CTCSS' },
  { value: 'DCS', label: 'DCS' },
  { value: 'DCS_INVERTED', label: 'DCS inverted' },
];

function ToneField({ ch, side, onChange, disabled }: { ch: CodeplugChannel; side: 'rx' | 'tx'; onChange: (c: CodeplugChannel) => void; disabled?: boolean }) {
  const type = ch[`${side}_tone_type`] ?? 'NONE';
  const value = ch[`${side}_tone`];
  const values = type === 'CTCSS' ? CTCSS_TONES : type === 'NONE' ? [] : DCS_CODES;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-gray-400">{side.toUpperCase()} tone</span>
      <select className={input} aria-label={`${side.toUpperCase()} tone type`} value={type} disabled={disabled} onChange={(e) => onChange(withTone(ch, side, e.target.value as ToneType))}>
        {TONE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      {type !== 'NONE' && (
        <select className={input} aria-label={`${side.toUpperCase()} tone`} value={value ?? ''} disabled={disabled} onChange={(e) => onChange({ ...ch, [`${side}_tone`]: e.target.value })}>
          {values.map((v) => <option key={v} value={v}>{type === 'CTCSS' ? `${v} Hz` : `D${v}${type === 'DCS_INVERTED' ? 'I' : 'N'}`}</option>)}
        </select>
      )}
    </span>
  );
}

function ChannelRow({ ch, codeplug, onChange, onDelete, disabled }: {
  ch: CodeplugChannel;
  codeplug: Codeplug;
  onChange: (c: CodeplugChannel) => void;
  onDelete: () => void;
  disabled?: boolean;
}) {
  const set = (patch: Partial<CodeplugChannel>) => onChange({ ...ch, ...patch });
  return (
    <tr className="align-top border-t border-gray-700" data-channel-row={ch.number}>
      <td className="py-2 pr-2 text-gray-400">{ch.number}</td>
      <td className="py-2 pr-2">
        <input aria-label="Channel name" className={`${input} w-40`} value={ch.name} maxLength={LIMITS.name} disabled={disabled} onChange={(e) => set({ name: e.target.value })} />
      </td>
      <td className="py-2 pr-2">
        <select aria-label="Mode" className={input} value={ch.mode} disabled={disabled} onChange={(e) => onChange(withMode(ch, e.target.value as ChannelMode))}>
          <option value="DIGITAL">Digital (DMR)</option>
          <option value="ANALOG">Analog (FM)</option>
        </select>
      </td>
      <td className="py-2 pr-2 space-y-1">
        <div className="flex items-center gap-1"><span className="text-xs text-gray-400 w-6">RX</span><MhzInput label="RX frequency (MHz)" hz={ch.rx_frequency_hz} disabled={disabled} onChange={(hz) => set({ rx_frequency_hz: hz })} /></div>
        <div className="flex items-center gap-1"><span className="text-xs text-gray-400 w-6">TX</span><MhzInput label="TX frequency (MHz)" hz={ch.tx_frequency_hz} disabled={disabled} onChange={(hz) => set({ tx_frequency_hz: hz })} /></div>
      </td>
      <td className="py-2 pr-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-300">
          <label className="inline-flex items-center gap-1">Power
            <select className={input} value={ch.tx_power} disabled={disabled} onChange={(e) => set({ tx_power: e.target.value as 'LOW' | 'HIGH' })}>
              <option value="LOW">Low</option><option value="HIGH">High</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-1">TX admit
            <select className={input} value={ch.tx_admit} disabled={disabled} onChange={(e) => set({ tx_admit: e.target.value as 'ALWAYS' | 'CHANNEL_FREE' })}>
              <option value="ALWAYS">Always</option><option value="CHANNEL_FREE">Channel free</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-1"><input type="checkbox" checked={ch.rx_only} disabled={disabled} onChange={(e) => set({ rx_only: e.target.checked })} />RX only</label>
          {ch.mode === 'DIGITAL' ? (
            <>
              <label className="inline-flex items-center gap-1">Color code
                <select className={input} value={ch.color_code ?? 1} disabled={disabled} onChange={(e) => set({ color_code: Number(e.target.value) })}>
                  {Array.from({ length: 16 }, (_, i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </label>
              <label className="inline-flex items-center gap-1">Slot
                <select className={input} value={ch.time_slot ?? 1} disabled={disabled} onChange={(e) => set({ time_slot: Number(e.target.value) })}>
                  <option value={1}>1</option><option value={2}>2</option>
                </select>
              </label>
              <label className="inline-flex items-center gap-1">TX contact
                <select className={input} value={ch.tx_contact ?? 0} disabled={disabled} onChange={(e) => set({ tx_contact: Number(e.target.value) })}>
                  <option value={0}>None (no PTT)</option>
                  {codeplug.contacts.map((c) => <option key={c.number} value={c.number}>{c.number}: {c.name}</option>)}
                </select>
              </label>
              <label className="inline-flex items-center gap-1">RX group list
                <select className={input} value={ch.rx_group_list ?? 0} disabled={disabled} onChange={(e) => set({ rx_group_list: Number(e.target.value) })}>
                  <option value={0}>None</option>
                  {codeplug.rx_group_lists.map((l) => <option key={l.number} value={l.number}>{l.number}: {l.name}</option>)}
                </select>
              </label>
              <label className="inline-flex items-center gap-1"><input type="checkbox" checked={ch.dcdm ?? false} disabled={disabled} onChange={(e) => set({ dcdm: e.target.checked })} />DCDM</label>
            </>
          ) : (
            <>
              <label className="inline-flex items-center gap-1">Bandwidth
                <select className={input} value={ch.bandwidth ?? 'NARROW'} disabled={disabled} onChange={(e) => set({ bandwidth: e.target.value as 'NARROW' | 'WIDE' })}>
                  <option value="NARROW">12.5 kHz</option><option value="WIDE">25 kHz</option>
                </select>
              </label>
              <label className="inline-flex items-center gap-1">Squelch
                <select className={input} value={ch.squelch ?? 0} disabled={disabled} onChange={(e) => set({ squelch: Number(e.target.value) })}>
                  {Array.from({ length: 10 }, (_, i) => <option key={i} value={i}>{i}</option>)}
                </select>
              </label>
              <ToneField ch={ch} side="rx" onChange={onChange} disabled={disabled} />
              <ToneField ch={ch} side="tx" onChange={onChange} disabled={disabled} />
              <label className="inline-flex items-center gap-1"><input type="checkbox" checked={ch.tail_elimination ?? false} disabled={disabled} onChange={(e) => set({ tail_elimination: e.target.checked })} />Tail elimination</label>
              <label className="inline-flex items-center gap-1">Phase
                <select className={input} value={ch.tail_phase ?? 120} disabled={disabled} onChange={(e) => set({ tail_phase: Number(e.target.value) })}>
                  <option value={120}>120°</option><option value={180}>180°</option>
                </select>
              </label>
            </>
          )}
        </div>
      </td>
      <td className="py-2">
        {!disabled && <button type="button" className={`${smallBtn} bg-red-800 hover:bg-red-700 text-white`} onClick={onDelete}>Delete</button>}
      </td>
    </tr>
  );
}

export default function CodeplugEditor({ value, onChange, disabled }: { value: Codeplug; onChange: (c: Codeplug) => void; disabled?: boolean }) {
  const [tab, setTab] = useState<Tab>('zones');
  const [filter, setFilter] = useState('');
  const c = value;
  const problems = codeplugProblems(c);
  const channelOptions = c.channels.map((ch) => ({ value: ch.number, label: `${ch.number}: ${ch.name}` }));
  const groupContacts = c.contacts.filter((x) => x.call_type === 'GROUP').map((x) => ({ value: x.number, label: `${x.number}: ${x.name}` }));
  const channelName = (n: number) => `${n}: ${c.channels[n - 1]?.name ?? '?'}`;
  const contactName = (n: number) => `${n}: ${c.contacts[n - 1]?.name ?? '?'}`;

  const tabs: Array<[Tab, string, number, number]> = [
    ['zones', 'Zones', c.zones.length, LIMITS.zones],
    ['channels', 'Channels', c.channels.length, LIMITS.channels],
    ['contacts', 'Contacts', c.contacts.length, LIMITS.contacts],
    ['lists', 'RX group lists', c.rx_group_lists.length, LIMITS.rxGroupLists],
  ];

  const setContact = (i: number, patch: Partial<CodeplugContact>) =>
    onChange({ ...c, contacts: c.contacts.map((x, k) => (k === i ? { ...x, ...patch } : x)) });

  return (
    <div className="space-y-3" data-codeplug-editor>
      <div className="flex flex-wrap gap-2">
        {tabs.map(([id, label, n, max]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`px-3 py-1.5 rounded text-sm font-semibold ${tab === id ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
          >
            {label} <span className="text-xs font-normal opacity-80">{n}/{max}</span>
          </button>
        ))}
      </div>

      {tab === 'zones' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">The radio keeps at least one zone; each zone lists 1-{LIMITS.channelsPerZone} channels in display order. Zone names up to {LIMITS.name} characters.</p>
          {c.zones.map((z, i) => (
            <div key={z.number} className="flex flex-wrap items-start gap-2 border-t border-gray-700 pt-2" data-zone-row={z.number}>
              <span className="text-gray-400 w-6 pt-1">{z.number}</span>
              <input aria-label="Zone name" className={`${input} w-48`} value={z.name} maxLength={LIMITS.name} disabled={disabled}
                onChange={(e) => onChange({ ...c, zones: c.zones.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)) })} />
              <div className="flex-1 min-w-[260px]">
                <NumberChips values={z.channels} label={channelName} options={channelOptions} max={LIMITS.channelsPerZone} disabled={disabled}
                  onChange={(channels) => onChange({ ...c, zones: c.zones.map((x, k) => (k === i ? { ...x, channels } : x)) })} />
              </div>
              {!disabled && (
                <span className="flex gap-1">
                  <button type="button" className={`${smallBtn} bg-gray-700 text-white`} onClick={() => onChange(moveZone(c, z.number, -1))} disabled={i === 0}>↑</button>
                  <button type="button" className={`${smallBtn} bg-gray-700 text-white`} onClick={() => onChange(moveZone(c, z.number, 1))} disabled={i === c.zones.length - 1}>↓</button>
                  <button type="button" className={`${smallBtn} bg-red-800 hover:bg-red-700 text-white`} onClick={() => onChange(deleteZone(c, z.number))}>Delete</button>
                </span>
              )}
            </div>
          ))}
          {!disabled && c.zones.length < LIMITS.zones && (
            <button type="button" className={`${smallBtn} bg-green-700 hover:bg-green-600 text-white`}
              onClick={() => onChange({ ...c, zones: [...c.zones, { number: c.zones.length + 1, name: `Zone ${c.zones.length + 1}`, channels: [] }] })}>
              + Add zone
            </button>
          )}
        </div>
      )}

      {tab === 'channels' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">
            Frequencies in MHz (136-174 or 350-480; the radio's DMR module band is narrower and is checked on the radio). A
            DIGITAL channel without a TX contact cannot transmit. Deleting a channel removes it from every zone.
          </p>
          {c.channels.length > 20 && (
            <input className={`${input} w-64`} placeholder="Filter channels by name…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400">
                  <th className="pr-2">#</th><th className="pr-2">Name</th><th className="pr-2">Mode</th><th className="pr-2">Frequency</th><th className="pr-2">Settings</th><th />
                </tr>
              </thead>
              <tbody>
                {c.channels
                  .filter((ch) => !filter || ch.name.toLowerCase().includes(filter.toLowerCase()))
                  .map((ch) => (
                    <ChannelRow key={ch.number} ch={ch} codeplug={c} disabled={disabled}
                      onChange={(next) => onChange({ ...c, channels: c.channels.map((x) => (x.number === ch.number ? next : x)) })}
                      onDelete={() => onChange(deleteChannel(c, ch.number))} />
                  ))}
              </tbody>
            </table>
          </div>
          {!disabled && c.channels.length < LIMITS.channels && (
            <span className="flex gap-2">
              <button type="button" className={`${smallBtn} bg-green-700 hover:bg-green-600 text-white`} onClick={() => onChange({ ...c, channels: [...c.channels, newChannel(c.channels.length + 1, 'DIGITAL')] })}>+ Digital channel</button>
              <button type="button" className={`${smallBtn} bg-green-700 hover:bg-green-600 text-white`} onClick={() => onChange({ ...c, channels: [...c.channels, newChannel(c.channels.length + 1, 'ANALOG')] })}>+ Analog channel</button>
            </span>
          )}
        </div>
      )}

      {tab === 'contacts' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">Digital call targets. Private and group IDs 1-{LIMITS.individualCallId}; an All call contact always uses {LIMITS.allCallId}.</p>
          {c.contacts.map((x, i) => (
            <div key={x.number} className="flex flex-wrap items-center gap-2 border-t border-gray-700 pt-2" data-contact-row={x.number}>
              <span className="text-gray-400 w-6">{x.number}</span>
              <input aria-label="Contact name" className={`${input} w-44`} value={x.name} maxLength={LIMITS.name} disabled={disabled} onChange={(e) => setContact(i, { name: e.target.value })} />
              <select aria-label="Call type" className={input} value={x.call_type} disabled={disabled}
                onChange={(e) => {
                  const call_type = e.target.value as CodeplugContact['call_type'];
                  setContact(i, { call_type, call_id: call_type === 'ALL' ? LIMITS.allCallId : x.call_type === 'ALL' ? 1 : x.call_id });
                }}>
                <option value="GROUP">Group call</option><option value="PRIVATE">Private call</option><option value="ALL">All call</option>
              </select>
              <input aria-label="Call ID" type="number" className={`${input} w-32`} value={x.call_id} min={1} max={LIMITS.individualCallId} disabled={disabled || x.call_type === 'ALL'}
                onChange={(e) => setContact(i, { call_id: Number(e.target.value) })} />
              {!disabled && <button type="button" className={`${smallBtn} bg-red-800 hover:bg-red-700 text-white`} onClick={() => onChange(deleteContact(c, x.number))}>Delete</button>}
            </div>
          ))}
          {!disabled && c.contacts.length < LIMITS.contacts && (
            <button type="button" className={`${smallBtn} bg-green-700 hover:bg-green-600 text-white`} onClick={() => onChange({ ...c, contacts: [...c.contacts, newContact(c.contacts.length + 1)] })}>+ Add contact</button>
          )}
        </div>
      )}

      {tab === 'lists' && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500">A digital channel with an RX group list also receives these group contacts (1-{LIMITS.rxGroupListSize} group contacts per list).</p>
          {c.rx_group_lists.map((l, i) => (
            <div key={l.number} className="flex flex-wrap items-start gap-2 border-t border-gray-700 pt-2" data-list-row={l.number}>
              <span className="text-gray-400 w-6 pt-1">{l.number}</span>
              <input aria-label="RX group list name" className={`${input} w-44`} value={l.name} maxLength={LIMITS.name} disabled={disabled}
                onChange={(e) => onChange({ ...c, rx_group_lists: c.rx_group_lists.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)) })} />
              <div className="flex-1 min-w-[260px]">
                <NumberChips values={l.contacts} label={contactName} options={groupContacts} max={LIMITS.rxGroupListSize} disabled={disabled}
                  onChange={(contacts) => onChange({ ...c, rx_group_lists: c.rx_group_lists.map((x, k) => (k === i ? { ...x, contacts } : x)) })} />
              </div>
              {!disabled && <button type="button" className={`${smallBtn} bg-red-800 hover:bg-red-700 text-white`} onClick={() => onChange(deleteRxGroupList(c, l.number))}>Delete</button>}
            </div>
          ))}
          {!disabled && c.rx_group_lists.length < LIMITS.rxGroupLists && (
            <button type="button" className={`${smallBtn} bg-green-700 hover:bg-green-600 text-white`}
              onClick={() => onChange({ ...c, rx_group_lists: [...c.rx_group_lists, { number: c.rx_group_lists.length + 1, name: `RX list ${c.rx_group_lists.length + 1}`, contacts: [] }] })}>
              + Add RX group list
            </button>
          )}
        </div>
      )}

      {problems.length > 0 && (
        <div className="rounded border border-amber-700 bg-amber-900/20 p-2 text-xs text-amber-200" data-codeplug-problems>
          <p className="font-semibold">{problems.length} problem(s) must be fixed before saving:</p>
          <ul className="list-disc ml-5">
            {problems.slice(0, 8).map((p, i) => <li key={i}>{p}</li>)}
            {problems.length > 8 && <li>… and {problems.length - 8} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
